import { MessageStatsCounts } from "../../domain/use_cases/overview/getMessagesStats";
import JobWithContact from "../../domain/models/@aggregates/JobWithContact";
import { JobType } from "@leedpulse-monorepo/enums";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import moment from "moment";
import { Table } from "antd";

const round2digits = (num: number): number => {
  try {
    const p = Math.pow(10, 2);
    return Math.round(num * p) / p;
  } catch (e) {
    console.error('Error while rounding number to 2 digits', e);
    return num;
  }
};

const formatCredit = (value: number): string => {
  const formatted = value.toFixed(2);
  return formatted.endsWith('.00') ? formatted.replace('.00', '') : formatted;
};

// get tag color by interest level
const getTagColor = (interestLevel: string) => {
  switch (interestLevel) {
    case 'interested':
      return 'green';
    case 'considering':
      return 'purple';
    case 'not_interested':
      return 'red';
    case 'not_available':
      return 'yellow';
    case 'not_responsive':
      return 'orange';
    default:
      return 'blue';
  }

};

export { round2digits, getTagColor, formatCredit };

// generate a duplicate name based on an array of names
export const generateDuplicatedName = (name: string, names: string[]) => {
  const isDuplicated = names.includes(name);
  if (!isDuplicated) {
    return name;
  }

  let counter = 1;
  let newName = `${name} - ${counter}`;
  const duplicatedNames = names.filter((n) => n.includes(name));
  while (duplicatedNames.includes(newName)) {
    counter++;
    newName = `${name} - ${counter}`;
  }
  return newName;
};

// generate data for the action chart
export const generateActionChartData = (jobs: JobWithContact[], days: number, senderFilter?: string | null): {
  categories: string[],
  series: { name: string; data: number[]; color?: string; jobType?: JobType }[]
} => {
  dayjs.locale('fr');
  const today = dayjs().startOf("day");

  // Helper function to check if a job type is a followUp with days (follow_up_X_days where X is any number)
  const isFollowUpWithDays = (jobType: string): boolean => {
    return /follow[_-]?up[_-]?\d+[_-]?days?/i.test(jobType);
  };

  // Helper function to normalize job type key (group followUp types together)
  const getJobTypeKey = (jobType: string): string => {
    return isFollowUpWithDays(jobType) ? 'followUp' : jobType;
  };

  // Helper function to get series name based on job type
  const getSeriesName = (jobTypeKey: string, sender?: string): string => {
    const senderSuffix = sender ? ` avec +${sender}` : '';

    if (jobTypeKey === 'followUp') {
      return `Suivi${senderSuffix}`;
    }

    const actualJobType = jobTypeKey as JobType;
    switch (actualJobType) {
      case JobType.firstContact:
        return `Premier contact${senderSuffix}`;
      case JobType.followUpToDo:
        return `Qualification en attente de réponse${senderSuffix}`;
      case JobType.followUpDone:
        return `Suivi terminé${senderSuffix}`;
      case JobType.reply:
        return `Nouvelle réponse${senderSuffix}`;
      case JobType.reminderNoresponse:
        return `Rappel si pas de réponse${senderSuffix}`;
      default:
        return `${jobTypeKey}${senderSuffix}`;
    }
  };

  // Filter jobs by sender if specified
  const filteredJobs = senderFilter
    ? jobs.filter(job => job.contact.contact.sender === senderFilter)
    : jobs;

  const labels = Array(days)
    .fill(0)
    .map((_, i) => today.add(i, "day").format("DD MMMM"));

  // Count jobs by type and optionally by sender
  // Structure: { [sender?]: { [jobType]: number[] } }
  const counts: { [key: string]: { [key: string]: number[] } } = {};

  filteredJobs.forEach((job) => {
    const jobDate = dayjs(job.timestamp).format("DD MMMM");
    const jobTypeKey = getJobTypeKey(job.type);
    const groupKey = senderFilter ? job.contact.contact.sender : 'all';

    if (!counts[groupKey]) {
      counts[groupKey] = {};
    }

    if (!counts[groupKey][jobTypeKey]) {
      counts[groupKey][jobTypeKey] = Array(days).fill(0);
    }

    const index = labels.indexOf(jobDate);
    if (index !== -1) {
      counts[groupKey][jobTypeKey][index]++;
    }
  });

  // Transform data for the graph
  const series: { name: string; data: number[]; jobType?: JobType }[] = [];

  Object.keys(counts).forEach((groupKey) => {
    const jobTypes = counts[groupKey];
    const sender = senderFilter ? groupKey : undefined;

    Object.keys(jobTypes).forEach((jobTypeKey) => {
      const data = jobTypes[jobTypeKey];
      const hasData = data.some(count => count > 0);

      if (hasData) {
        const seriesName = getSeriesName(jobTypeKey, sender);
        const jobType = jobTypeKey === 'followUp'
          ? JobType.followUp30Days
          : (jobTypeKey as JobType);

        series.push({
          name: seriesName,
          data: data,
          jobType: jobType,
        });
      }
    });
  });

  return {
    categories: labels,
    series
  };
};

export const generateOverviewChartData = (messagesStatsCounts: MessageStatsCounts[]) => {
  const categories = messagesStatsCounts.map(messagesStat => moment(messagesStat.timestamp).format('ddd D MMMM'));
  const smsSent = messagesStatsCounts.map(messagesStat => messagesStat.smsSentCount);
  const smsReceived = messagesStatsCounts.map(messagesStat => messagesStat.smsReceivedCount);
  const waSent = messagesStatsCounts.map(messagesStat => messagesStat.whatsAppMessageSentCount);
  const waReceived = messagesStatsCounts.map(messagesStat => messagesStat.whatsAppMessageReceivedCount);
  const callrSent = messagesStatsCounts.map(messagesStat => messagesStat.callrSmsSentCount);
  const callrReceived = messagesStatsCounts.map(messagesStat => messagesStat.callrSmsReceivedCount);
  const notDelivered = messagesStatsCounts.map(messagesStat => messagesStat.messagesNotDeliveredCount);

  return {
    series: [
      { name: "SMS Envoyés", data: smsSent },
      { name: "SMS Reçus", data: smsReceived },
      { name: "WhatsApp Envoyés", data: waSent },
      { name: "WhatsApp Reçus", data: waReceived },
      { name: "SMS Callr Reçus", data: callrReceived },
      { name: "SMS Callr Envoyés", data: callrSent },
      { name: "Non Livrés", data: notDelivered },
    ],
    categories: categories
  }
};

/** Types definitions for advancedOnRow (better than any) **/
type TableOnRow = NonNullable<React.ComponentProps<typeof Table>['onRow']>;
type TableOnRowProps = ReturnType<TableOnRow>;
type TableOnRowEvent = Parameters<NonNullable<TableOnRowProps['onClick']>>[0];
type TableOnRowBeforeHandler = (event: TableOnRowEvent) => void | false;

/**
 * Advanced onRow function for tables.
 * It allow middle / cmd / ctrl click on rows to open in a new tab
 * 
 * @param param0 
 * @returns 
 */
export const advancedOnRow = (
  {
    url,
    navigate,
    onMouseDownBefore,
    onClickBefore,
    ...onRowProps
  }: {
    url: string,
    navigate: (url: string) => void,
    onMouseDownBefore?: TableOnRowBeforeHandler,
    onClickBefore?: TableOnRowBeforeHandler,
  } & Omit<TableOnRowProps, 'onClick' | 'onMouseDown'>
): TableOnRowProps => {
  return {
    onMouseDown: (e) => {
      if (onMouseDownBefore?.(e) === false) return;

      if (e.button === 1) {
        e.preventDefault();
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    onClick: (e) => {
      if (onClickBefore?.(e) === false) return;

      const isCtrlOrCmdClick = e?.ctrlKey || e?.metaKey;

      if (isCtrlOrCmdClick) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(url);
    },
    ...onRowProps
  };
};